import { z } from 'zod';

import { instantlyConnectionSchema } from './console.js';
import { promptNameSchema } from './prompts.js';

/**
 * Wizard "Nueva empresa" (F4a). Casi todo el alta usa endpoints que ya existían (clientes,
 * empresas, conexiones de Instantly, integraciones, dossier, invitaciones, funciones); acá están
 * los tres que faltaban: registrar el webhook en Instantly, copiar prompts de otra empresa y el
 * checklist (lo que ya está hecho en la plataforma + lo que falta hacer en el motor n8n hasta que
 * llegue la consolidación de F4).
 */

// ------------------------------------------------------------------ webhook de Instantly

export const INSTANTLY_WEBHOOK_NAME = 'Plataforma ETW (all_events)';

export const registerWebhookResponseSchema = z.object({
  connection: instantlyConnectionSchema,
  webhookId: z.string(),
  /** `created`: webhook nuevo; `updated`: ya había uno a nuestra URL y se le cambió el secreto. */
  action: z.enum(['created', 'updated']),
});
export type RegisterWebhookResponse = z.infer<typeof registerWebhookResponseSchema>;

// ------------------------------------------------------------------ prompts desde otra empresa

export const copyPromptsSchema = z.object({
  sourceTenantId: z.uuid(),
  /** Default: todos los prompts que tenga la empresa de origen. */
  names: z.array(promptNameSchema).min(1).optional(),
});
export type CopyPrompts = z.input<typeof copyPromptsSchema>;

export const copyPromptsResponseSchema = z.object({
  copied: z.array(
    z.object({
      name: promptNameSchema,
      /** Versión de origen en la plataforma (null si vino del stack). */
      fromVersion: z.number().int().nullable(),
      origin: z.enum(['published', 'draft', 'legacy']),
      draftVersion: z.number().int(),
    }),
  ),
  skipped: z.array(promptNameSchema),
});
export type CopyPromptsResponse = z.infer<typeof copyPromptsResponseSchema>;

// ------------------------------------------------------------------ checklist

export const onboardingStepSchema = z.object({
  key: z.string(),
  label: z.string(),
  done: z.boolean(),
  /** Qué falta o qué se encontró (en español). */
  detail: z.string().nullable(),
  /** Pasos opcionales no bloquean "lista para operar" (p. ej. Gmail si no hay bot). */
  optional: z.boolean(),
});
export type OnboardingStep = z.infer<typeof onboardingStepSchema>;

export const engineStepSchema = z.object({
  key: z.string(),
  label: z.string(),
  detail: z.string(),
  /** Comando de referencia (en el VPS o desde la raíz del repo). */
  command: z.string().nullable(),
});
export type EngineStep = z.infer<typeof engineStepSchema>;

export const onboardingChecklistSchema = z.object({
  tenantId: z.uuid(),
  slug: z.string(),
  name: z.string(),
  status: z.string(),
  platform: z.array(onboardingStepSchema),
  engine: z.array(engineStepSchema),
  /** Todos los pasos NO opcionales de la plataforma hechos. */
  platformReady: z.boolean(),
});
export type OnboardingChecklist = z.infer<typeof onboardingChecklistSchema>;

/**
 * Pasos del lado del motor (n8n) para una empresa nueva mientras cada empresa tenga su propio stack
 * (etapa 1). Se generan con el slug para poder copiar los comandos. Desaparecen con la consolidación
 * de F4 (un solo n8n parametrizado por tenant).
 */
export function engineChecklist(slug: string): EngineStep[] {
  const cli = `cd /opt/platform && docker compose -f docker-compose.prod.yml --env-file .env run --rm -T --no-deps platform-api node dist/cli.js`;
  return [
    {
      key: 'stack',
      label: 'Stack n8n de la empresa',
      detail: `Clonar un stack existente en /opt/${slug} (infra/.env propio, Postgres, n8n + worker), aplicar infra/sql/schema.sql + migraciones (infra/sql/MIGRATIONS.md) y sumar el hostname al Caddyfile compartido SIN sed -i (DNS gris en Cloudflare).`,
      command: null,
    },
    {
      key: 'workflows',
      label: 'Workflows (02, 03, 04, 06, 07, 09, 10, 11, 14, 15)',
      detail:
        'Importar con deploy-workflow.mjs (resuelve credenciales y sub-workflows por nombre). Cargar GEMINI_API_KEY, INSTANTLY_API_KEY, INSTANTLY_CAMPAIGN_ID y /opt/<stack>/secrets/sa.json (fuera de infra/).',
      command: 'node infra/scripts/deploy-workflow.mjs --help',
    },
    {
      key: 'legacy-roles',
      label: 'Roles platform_reader / platform_writer en la DB del stack',
      detail: `Sumar el contenedor de Postgres de ${slug} a STACKS en platform-legacy-roles.sh y correrlo (idempotente). Después, las integraciones legacy_db (reader y writer) con el CLI.`,
      command: `bash /opt/leads-etw/infra/scripts/platform-legacy-roles.sh && ${cli} integrations:set --tenant ${slug} --kind legacy_db --label "DB ${slug}" --config '{"schema":"leads","role":"reader"}' --secret-env SECRET`,
    },
    {
      key: 'n8n-integration',
      label: 'Integración n8n (ejecuciones en Operaciones)',
      detail: 'API key de n8n del stack como integración n8n (red interna, nombre de contenedor).',
      command: `${cli} integrations:set --tenant ${slug} --kind n8n --label "n8n ${slug}" --config '{"baseUrl":"http://${slug}-n8n-1:5678","instance":"${slug}"}' --secret-env SECRET`,
    },
    {
      key: 'ingest-key',
      label: 'API key de ingesta en el volumen de n8n',
      detail: `Crear la key de la empresa en /opt/platform/ingest-${slug}.key (600) y sumar la instancia a patch-entitlements-gate.mjs (gate de WF02/03/07/11/14) y, si manda por Gmail, a patch-wf10-platform-ingest.mjs.`,
      command: `${cli} apikey:create --name "n8n ${slug}" --tenant ${slug} > /root/k.json  # extraer apiKey a /opt/platform/ingest-${slug}.key`,
    },
    {
      key: 'entitlements-gate',
      label: 'Gate de entitlements (fail-open)',
      detail:
        'Agregar la instancia a INSTANCES en infra/scripts/patch-entitlements-gate.mjs y correrlo: instala la key y el gate en cada workflow que hace trabajo nuevo.',
      command: `node infra/scripts/patch-entitlements-gate.mjs --instance ${slug}`,
    },
    {
      key: 'scraper',
      label: 'maps-scraper: queries, env y cron con heartbeat',
      detail: `queries/${slug}.txt (categorías intercaladas), env/${slug}.env (DELIVERY_WEBHOOK_URL del WF14 + token) y la línea de /etc/cron.d envuelta en with-heartbeat.sh; registrar la expectativa.`,
      command: `${cli} heartbeat:register --job scraper --tenant ${slug} --interval 86400 --grace 10800`,
    },
    {
      key: 'instantly-engine',
      label: 'Instantly del lado del motor',
      detail:
        'Buzones conectados y en warmup, campaña con su schedule y, si hay bot, el webhook reply_received por campaña que dispara el WF10 (es aparte del all_events de la plataforma).',
      command: null,
    },
    {
      key: 'backups',
      label: 'Backup diario del stack',
      detail:
        'Cron de backup (DB + volumen de n8n + .env) envuelto en with-heartbeat.sh y registrado; los clones estuvieron meses sin backup por no hacer este paso.',
      command: null,
    },
  ];
}
